import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getPublicPost, getPublicPosts, getTags } from '../api/galleryApi';
import Pagination from '../components/Pagination';
import GalleryLightbox from '../components/GalleryLightbox';
import VisitBookingModal from '../components/VisitBookingModal';
import './GalleryPage.css';

const PAGE_SIZE = 12;

// Fully public page — no login, no portal chrome. Anyone with a shared /gallery/:postId
// link can open it directly, same as sharing an Instagram post link.
const GalleryPage = () => {
  const { postId } = useParams();
  const navigate = useNavigate();

  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [topTags, setTopTags] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [permalinkError, setPermalinkError] = useState(false);
  const [showBooking, setShowBooking] = useState(false);

  useEffect(() => {
    getTags()
      .then((res) => setTopTags(res.data || []))
      .catch(() => setTopTags([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    getPublicPosts(page, PAGE_SIZE, search)
      .then((res) => {
        setPosts(res.data.content || []);
        setTotalPages(res.data.totalPages || 0);
      })
      .catch(() => {
        setPosts([]);
        setTotalPages(0);
      })
      .finally(() => setLoading(false));
  }, [page, search]);

  // Deep-linked post loads independently of the grid so a shared link shows content right away.
  useEffect(() => {
    if (!postId) {
      setSelectedPost(null);
      return;
    }
    setPermalinkError(false);
    getPublicPost(postId)
      .then((res) => setSelectedPost(res.data))
      .catch(() => setPermalinkError(true));
  }, [postId]);

  const openPost = useCallback((post) => {
    setSelectedPost(post);
    navigate(`/gallery/${post.id}`);
  }, [navigate]);

  const closeLightbox = useCallback(() => {
    setSelectedPost(null);
    navigate('/gallery');
  }, [navigate]);

  const handleTagClick = (tag) => {
    setSearch(tag);
    setPage(0);
  };

  return (
    <div className="gallery-page">
      <header className="gallery-page__header">
        <Link to="/" className="gallery-page__brand">
          <span>eYantra</span>
          <span className="gallery-page__brand-dot" />
          <span className="gallery-page__brand-sub">Gallery</span>
        </Link>
        <p className="gallery-page__tagline">A look at what we've delivered.</p>
        <button type="button" className="gallery-page__book-visit-btn" onClick={() => setShowBooking(true)}>
          📅 Book a Visit to Our Experience Center
        </button>
      </header>

      <div className="gallery-page__controls">
        <div className="gallery-page__search-box">
          <span className="gallery-page__search-icon" aria-hidden="true">🔍</span>
          <input
            type="text"
            aria-label="Search by tag"
            className="gallery-page__search"
            placeholder="Search by tag…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          />
        </div>

        {topTags.length > 0 && (
          <div className="gallery-page__top-tags">
            {topTags.map((tag) => (
              <button
                type="button" key={tag}
                className={`gallery-page__tag${search === tag ? ' is-active' : ''}`}
                onClick={() => { setSearch(tag); setPage(0); }}
              >
                {tag}
              </button>
            ))}
          </div>
        )}
      </div>

      {permalinkError && (
        <p className="gallery-page__notice">That post couldn't be found — it may have been removed.</p>
      )}

      {loading ? (
        <p className="gallery-page__status">Loading…</p>
      ) : posts.length === 0 ? (
        <p className="gallery-page__status">No photos found.</p>
      ) : (
        <div className="gallery-page__grid">
          {posts.map((post) => (
            <div key={post.id} className="gallery-card">
              <button type="button" className="gallery-card__image-btn" onClick={() => openPost(post)}>
                {post.images?.[0] && (
                  <img className="gallery-card__image" src={post.images[0].url} alt={post.caption || 'Gallery photo'} />
                )}
                <div className="gallery-card__overlay">
                  {post.caption && <span className="gallery-card__caption">{post.caption}</span>}
                </div>
              </button>
              {post.tags?.length > 0 && (
                <div className="gallery-card__tags">
                  {post.tags.map((tag) => (
                    <button type="button" key={tag} className="gallery-card__tag" onClick={() => handleTagClick(tag)}>
                      {tag}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
      )}

      {selectedPost && <GalleryLightbox post={selectedPost} onClose={closeLightbox} />}
      {showBooking && <VisitBookingModal onClose={() => setShowBooking(false)} />}
    </div>
  );
};

export default GalleryPage;
